import { useState, useEffect, useCallback, useContext, useRef } from 'react'
import PropTypes from 'prop-types'

import { toast } from 'sonner'
import ModelSelector from '../ModelSelector.jsx'
import { AppContext } from '../AppContext.tsx'
import { apiClient } from '../../lib/apiClient'
import ValidatorIcon from '../../components/microComponents/ValidatorIcon.jsx'
import InfoIcon from '../../components/icons/infocircle.svg?react'

const ApiKeyConfiguration = ({ 
  renderNavigation, 
  renderProgress,
  navigationProps,
  progressProps 
}) => {
  const { updateApiKeys } = useContext(AppContext)
  const [anthropicKey, setAnthropicKey] = useState('')
  const [openAIKey, setOpenAIKey] = useState('')
  const [geminiKey, setGeminiKey] = useState('')
  const [xaiKey, setXaiKey] = useState('')
  const [openrouterKey, setOpenrouterKey] = useState('')
  
  // Validation states: 'idle' | 'validating' | 'valid' | 'invalid'
  const [validationStates, setValidationStates] = useState({
    anthropic: 'idle',
    openai: 'idle',
    gemini: 'idle',
    xai: 'idle',
    openrouter: 'idle'
  })
  
  // Track error messages from validation
  const [errorMessages, setErrorMessages] = useState({
    anthropic: '',
    openai: '',
    gemini: '',
    xai: '',
    openrouter: ''
  })
  
  // Track models returned from API validation
  const [availableModelsByProvider, setAvailableModelsByProvider] = useState({
    anthropic: [],
    openai: [],
    gemini: [],
    xai: [],
    openrouter: []
  })
  
  // Track which models are available based on valid keys
  const [availableModels, setAvailableModels] = useState([])
  const [selectedModel, setSelectedModel] = useState(null)
  
  // Real API validation function
  const validateApiKey = useCallback(async (key, provider) => {
    try {
      const result = await apiClient.post('/api/validate-api-key', {
        provider: provider,
        api_key: key
      })
      return result
    } catch (error) {
      console.error('API key validation error:', error)
      return { is_valid: false, error_message: 'Validation failed' }
    }
  }, [navigationProps.authSession?.access_token, navigationProps.authSession?.refresh_token])

  // On component mount only: reset state and fetch API keys
  useEffect(() => {
    // Reset all state to fresh
    setSelectedModel(null)
    setAvailableModels([])
    setAvailableModelsByProvider({ anthropic: [], openai: [], gemini: [], xai: [], openrouter: [] })
    setValidationStates({ anthropic: 'idle', openai: 'idle', gemini: 'idle', xai: 'idle', openrouter: 'idle' })
    setErrorMessages({ anthropic: '', openai: '', gemini: '', xai: '', openrouter: '' })
    setAnthropicKey('')
    setOpenAIKey('')
    setGeminiKey('')
    setXaiKey('')
    setOpenrouterKey('')
    
    // Fetch existing API keys
    const loadKeys = async () => {
      if (!navigationProps.authSession) return
      
      // Show loading state for all providers while fetching settings
      setValidationStates({ anthropic: 'validating', openai: 'validating', gemini: 'validating', xai: 'validating', openrouter: 'validating' })
      
      try {
        const data = await apiClient.get('/api/user-settings')
          
        // Handle Anthropic key
        if (data.anthropic_apikey) {
          setAnthropicKey(data.anthropic_apikey)
          // Validate to get available models
          validateApiKey(data.anthropic_apikey, 'anthropic').then(result => {
            if (result.is_valid) {
              setValidationStates(prev => ({ ...prev, anthropic: 'valid' }))
              if (result.available_models) {
                setAvailableModelsByProvider(prev => ({
                  ...prev,
                  anthropic: result.available_models
                }))
              }
            } else {
              setValidationStates(prev => ({ ...prev, anthropic: 'invalid' }))
              if (result.error_message) {
                setErrorMessages(prev => ({ ...prev, anthropic: result.error_message }))
              }
            }
          })
        } else {
          // No Anthropic key, reset to idle
          setValidationStates(prev => ({ ...prev, anthropic: 'idle' }))
        }
        
        // Handle OpenAI key
        if (data.openai_apikey) {
          setOpenAIKey(data.openai_apikey)
          // Validate to get available models
          validateApiKey(data.openai_apikey, 'openai').then(result => {
            if (result.is_valid) {
              setValidationStates(prev => ({ ...prev, openai: 'valid' }))
              if (result.available_models) {
                setAvailableModelsByProvider(prev => ({
                  ...prev,
                  openai: result.available_models
                }))
              }
            } else {
              setValidationStates(prev => ({ ...prev, openai: 'invalid' }))
              if (result.error_message) {
                setErrorMessages(prev => ({ ...prev, openai: result.error_message }))
              }
            }
          })
        } else {
          // No OpenAI key, reset to idle
          setValidationStates(prev => ({ ...prev, openai: 'idle' }))
        }
        
        // Handle Gemini key
        if (data.gemini_apikey) {
          setGeminiKey(data.gemini_apikey)
          // Validate to get available models
          validateApiKey(data.gemini_apikey, 'gemini').then(result => {
            if (result.is_valid) {
              setValidationStates(prev => ({ ...prev, gemini: 'valid' }))
              if (result.available_models) {
                setAvailableModelsByProvider(prev => ({
                  ...prev,
                  gemini: result.available_models
                }))
              }
            } else {
              setValidationStates(prev => ({ ...prev, gemini: 'invalid' }))
              if (result.error_message) {
                setErrorMessages(prev => ({ ...prev, gemini: result.error_message }))
              }
            }
          })
        } else {
          // No Gemini key, reset to idle
          setValidationStates(prev => ({ ...prev, gemini: 'idle' }))
        }
          
        // Handle xAI key
        if (data.xai_apikey) {
          setXaiKey(data.xai_apikey)
          // Validate to get available models
          validateApiKey(data.xai_apikey, 'xai').then(result => {
            if (result.is_valid) {
              setValidationStates(prev => ({ ...prev, xai: 'valid' }))
              if (result.available_models) {
                setAvailableModelsByProvider(prev => ({
                  ...prev,
                  xai: result.available_models
                }))
              }
            } else {
              setValidationStates(prev => ({ ...prev, xai: 'invalid' }))
              if (result.error_message) {
                setErrorMessages(prev => ({ ...prev, xai: result.error_message }))
              }
            }
          })
        } else {
          // No xAI key, reset to idle
          setValidationStates(prev => ({ ...prev, xai: 'idle' }))
        }
        
        // Handle OpenRouter key
        if (data.openrouter_apikey) {
          setOpenrouterKey(data.openrouter_apikey)
          validateApiKey(data.openrouter_apikey, 'openrouter').then(result => {
            if (result.is_valid) {
              setValidationStates(prev => ({ ...prev, openrouter: 'valid' }))
              if (result.available_models) {
                setAvailableModelsByProvider(prev => ({
                  ...prev,
                  openrouter: result.available_models
                }))
              }
            } else {
              setValidationStates(prev => ({ ...prev, openrouter: 'invalid' }))
              if (result.error_message) {
                setErrorMessages(prev => ({ ...prev, openrouter: result.error_message }))
              }
            }
          })
        } else {
          // No OpenRouter key, reset to idle
          setValidationStates(prev => ({ ...prev, openrouter: 'idle' }))
        }
      } catch (error) {
        console.error('Failed to fetch API keys:', error)
        // Reset to idle on error
        setValidationStates({ anthropic: 'idle', openai: 'idle', gemini: 'idle', xai: 'idle', openrouter: 'idle' })
      }
    }
    
    loadKeys()
  }, []) // Empty dependency array - only run on mount
  
  // Debounce timer refs
  const debounceTimers = useRef({})
  
  // Handle API key change with debounced validation
  const handleKeyChange = useCallback((value, provider) => {
    // Update the key value
    switch (provider) {
      case 'openai':
        setOpenAIKey(value)
        break
      case 'anthropic':
        setAnthropicKey(value)
        break
      case 'gemini':
        setGeminiKey(value)
        break
      case 'xai':
        setXaiKey(value)
        break
      case 'openrouter':
        setOpenrouterKey(value)
        break
    }
    
    // Immediately clear models when key changes
    setAvailableModelsByProvider(prev => ({
      ...prev,
      [provider]: []
    }))
    
    // Clear selected model when key changes
    setSelectedModel(null)
    
    // Clear existing timer
    if (debounceTimers.current[provider]) {
      clearTimeout(debounceTimers.current[provider])
    }
    
    // Reset to idle if empty
    if (!value) {
      setValidationStates(prev => ({ ...prev, [provider]: 'idle' }))
      setErrorMessages(prev => ({ ...prev, [provider]: '' }))
      return
    }
    
    // Set validating state immediately
    setValidationStates(prev => ({ ...prev, [provider]: 'validating' }))
    
    // Set new debounce timer
    const newTimer = setTimeout(async () => {
      try {
        const result = await validateApiKey(value, provider)
        setValidationStates(prev => ({ 
          ...prev, 
          [provider]: result.is_valid ? 'valid' : 'invalid' 
        }))
        
        // Store or clear error message
        if (!result.is_valid && result.error_message) {
          setErrorMessages(prev => ({ ...prev, [provider]: result.error_message }))
        } else {
          setErrorMessages(prev => ({ ...prev, [provider]: '' }))
        }
        
        // Store the available models if validation was successful
        if (result.is_valid && result.available_models) {
          setAvailableModelsByProvider(prev => ({
            ...prev,
            [provider]: result.available_models
          }))
          
          // Show toast if models were found
          if (result.available_models.length > 0) {
            const count = result.available_models.length
            const providerName = provider === 'openai' ? 'OpenAI' : provider === 'anthropic' ? 'Anthropic' : provider === 'gemini' ? 'Gemini' : provider === 'xai' ? 'xAI' : 'OpenRouter'
            toast(`Found ${count} ${count === 1 ? 'model' : 'models'} available with your ${providerName} key`)
          }
          
          // Persist the validated API key
          const keysToUpdate = {}
          if (provider === 'openai') {
            keysToUpdate.openAIApiKey = value
          } else if (provider === 'anthropic') {
            keysToUpdate.anthropicApiKey = value
          } else if (provider === 'gemini') {
            keysToUpdate.geminiApiKey = value
          } else if (provider === 'xai') {
            keysToUpdate.xaiApiKey = value
          } else if (provider === 'openrouter') {
            keysToUpdate.openrouterApiKey = value
          }
          
          try {
            await updateApiKeys(keysToUpdate)
          } catch (error) {
            console.error('Failed to persist API key:', error)
            // Don't show error toast here as the key is still valid
          }
        } else {
          // Clear models if validation failed
          setAvailableModelsByProvider(prev => ({
            ...prev,
            [provider]: []
          }))
          // Clear selected model if no models available
          setSelectedModel(null)
        }
      } catch (error) {
        setValidationStates(prev => ({ ...prev, [provider]: 'invalid' }))
        setErrorMessages(prev => ({ ...prev, [provider]: 'Validation failed' }))
        setAvailableModelsByProvider(prev => ({
          ...prev,
          [provider]: []
        }))
        // Clear selected model on error
        setSelectedModel(null)
      }
    }, 500) // 500ms debounce
    
    debounceTimers.current[provider] = newTimer
  }, [validateApiKey, updateApiKeys])
  
  // Removed complex apiKeys syncing - we fetch directly on mount instead
  
  // Update available models when provider models change
  useEffect(() => {
    const models = []
    
    // Combine models from all validated providers
    if (availableModelsByProvider.anthropic.length > 0) {
      availableModelsByProvider.anthropic.forEach(model => {
        models.push({
          id: model.id,
          name: model.name,
          display_name: model.display_name,
          model_family: model.model_family || 'claude',
          description: model.description || 'Anthropic model'
        })
      })
    }
    
    if (availableModelsByProvider.openai.length > 0) {
      availableModelsByProvider.openai.forEach(model => {
        models.push({
          id: model.id,
          name: model.name,
          display_name: model.display_name,
          model_family: model.model_family || 'gpt',
          description: model.description || 'OpenAI model'
        })
      })
    }
    
    if (availableModelsByProvider.gemini.length > 0) {
      availableModelsByProvider.gemini.forEach(model => {
        models.push({
          id: model.id,
          name: model.name,
          display_name: model.display_name,
          model_family: model.model_family || 'gemini',
          description: model.description || 'Google Gemini model'
        })
      })
    }
    
    if (availableModelsByProvider.xai.length > 0) {
      availableModelsByProvider.xai.forEach(model => {
        models.push({
          id: model.id,
          name: model.name,
          display_name: model.display_name,
          model_family: model.model_family || 'grok',
          description: model.description || 'xAI Grok model'
        })
      })
    }
    
    if (availableModelsByProvider.openrouter.length > 0) {
      availableModelsByProvider.openrouter.forEach(model => {
        models.push({
          id: model.id,
          name: model.name,
          display_name: model.display_name,
          model_family: model.model_family || 'openrouter',
          description: model.description || 'OpenRouter model'
        })
      })
    }
    
    setAvailableModels(models)
    
    // Select first model if none selected
    if (models.length > 0 && !selectedModel) {
      setSelectedModel(models[0])
    } else if (models.length === 0) {
      setSelectedModel(null)
    }
  }, [availableModelsByProvider, selectedModel])
  
  const getTooltipText = (provider, status) => {
    if (status === 'idle') {
      let source = ''
      switch (provider) {
        case 'openai':
          source = 'Get your API key from platform.openai.com/api-keys.'
          break
        case 'anthropic':
          source = 'Get your API key from console.anthropic.com.'
          break
        case 'gemini':
          source = 'Get your API key from makersuite.google.com/app/apikey.'
          break
        case 'xai':
          source = 'Get your API key from console.x.ai.'
          break
        case 'openrouter':
          source = 'Get your API key from openrouter.ai/keys.'
          break
        case 'profile':
          source = 'This data is used to personalise future interactions. Data remains yours and is not used to build a profile outside of Consl.'
      }
      if (provider === 'profile') {
        return source;
      }

      return `API keys are encrypted and stored securely. Never exposed in browser storage.\n\n${source}`;
    } else if (status === 'validating') {
      return `Validating API key with ${provider.charAt(0).toUpperCase() + provider.slice(1)}`
    } else if (status === 'invalid') {
      if (errorMessages[provider]) {
        return errorMessages[provider]
      }
      return null
    } else if (status === 'valid') {
      const modelCount = availableModelsByProvider?.[provider]?.length || 0
      if (modelCount > 0) {
        return `${modelCount} ${modelCount === 1 ? 'model' : 'models'} available`
      } else {
        return 'No models available for the configured API key'
      }
    } else {
      return null
    }
  }

  // Status indicator component
  const StatusIndicator = ({tooltip}) => {
    return (
      <div className="relative group">
        <InfoIcon className="cursor-help size-4" />
        <div className="absolute right-0 bottom-full mb-spacing-2 w-[256px] p-spacing-2 bg-bg-tooltip text-[12px] text-primary rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 border border-border-default whitespace-pre-line">
          {tooltip ? tooltip : getTooltipText('profile', 'idle')}
        </div>
      </div>
    )
  }

  return (
    <div className="relative h-full w-full flex flex-row no-wrap items-center justify-center">
      {/* Main content */}
      <div className="space-y-spacing-4 max-w-[640px] w-[640px] px-spacing-2 md:px-0">
        <div className="space-y-spacing-4">
          <h1 className="text-dls-h1 text-primary text-center">Configure your models</h1>
          <p className="text-secondary text-left">Consl lets you configure your own API keys for the top model providers. This way you don't have to worry about quotas, or paying a subscription for tokens you're not using.</p>
        </div>

        <div className="bg-bg-card rounded-lg border border-border-default p-spacing-4">
          <div className="space-y-spacing-2">
            {/* OpenAI API key */}
            <div className="flex items-center justify-between">
              <label className="text-primary text-dls-body">OpenAI API key</label>
              <div className="flex items-center gap-spacing-2">
                <input
                  type="password"
                  value={openAIKey}
                  onChange={(e) => handleKeyChange(e.target.value, 'openai')}
                  placeholder="sk-..."
                  className="w-[194px] h-[24px] border border-border-default rounded px-spacing-2 py-spacing-1 bg-bg-input text-[14px] text-primary font-mono placeholder:text-secondary/50 focus:border-accent focus:outline-none transition-colors"
                />
                <ValidatorIcon status={validationStates.openai} tooltip={getTooltipText('openai', validationStates.openai)} />
              </div>
            </div>
            
            {/* Anthropic API key */}
            <div className="flex items-center justify-between">
              <label className="text-primary text-dls-body">Anthropic API key</label>
              <div className="flex items-center gap-spacing-2">
                <input
                  type="password"
                  value={anthropicKey}
                  onChange={(e) => handleKeyChange(e.target.value, 'anthropic')}
                  placeholder="sk-ant-..."
                  className="w-[194px] h-[24px] border border-border-default rounded px-spacing-2 py-spacing-1 bg-bg-input text-[14px] text-primary font-mono placeholder:text-secondary/50 focus:border-accent focus:outline-none transition-colors"
                />
                <ValidatorIcon status={validationStates.anthropic} tooltip={getTooltipText('anthropic', validationStates.anthropic)} />
              </div>
            </div>
            
            {/* Gemini API key */}
            <div className="flex items-center justify-between">
              <label className="text-primary text-dls-body">Gemini API key</label>
              <div className="flex items-center gap-spacing-2">
                <input
                  type="password"
                  value={geminiKey}
                  onChange={(e) => handleKeyChange(e.target.value, 'gemini')}
                  placeholder="AIza..."
                  className="w-[194px] h-[24px] border border-border-default rounded px-spacing-2 py-spacing-1 bg-bg-input text-[14px] text-primary font-mono placeholder:text-secondary/50 focus:border-accent focus:outline-none transition-colors"
                />
                <ValidatorIcon status={validationStates.gemini} tooltip={getTooltipText('gemini', validationStates.gemini)} />
              </div>
            </div>
            
            {/* xAI API key */}
            <div className="flex items-center justify-between">
              <label className="text-primary text-dls-body">xAI API key</label>
              <div className="flex items-center gap-spacing-2">
                <input
                  type="password"
                  value={xaiKey}
                  onChange={(e) => handleKeyChange(e.target.value, 'xai')}
                  placeholder="xai-..."
                  className="w-[194px] h-[24px] border border-border-default rounded px-spacing-2 py-spacing-1 bg-bg-input text-[14px] text-primary font-mono placeholder:text-secondary/50 focus:border-accent focus:outline-none transition-colors"
                />
                <ValidatorIcon status={validationStates.xai} tooltip={getTooltipText('xai', validationStates.xai)} />
              </div>
            </div>
            
            {/* OpenRouter API key */}
            <div className="flex items-center justify-between">
              <label className="text-primary text-dls-body">OpenRouter API key</label>
              <div className="flex items-center gap-spacing-2">
                <input
                  type="password"
                  value={openrouterKey}
                  onChange={(e) => handleKeyChange(e.target.value, 'openrouter')}
                  placeholder="sk-or-..."
                  className="w-[194px] h-[24px] border border-border-default rounded px-spacing-2 py-spacing-1 bg-bg-input text-[14px] text-primary font-mono placeholder:text-secondary/50 focus:border-accent focus:outline-none transition-colors"
                />
                <ValidatorIcon status={validationStates.openrouter} tooltip={getTooltipText('openrouter', validationStates.openrouter)} />
              </div>
            </div>
            
            {/* Model selector */}
            <div className="mt-spacing-4 flex justify-end">
              <ModelSelector
                position="top-right"
                models={availableModels}
                selectedModel={selectedModel}
                setSelectedModel={setSelectedModel}
                showTokenCount={false}
              />
            </div>
          </div>
        </div>
        
        <div className="w-full flex justify-center items-center relative mt-spacing-6">
          {renderProgress(progressProps)}
          {renderNavigation({
            ...navigationProps,
            canProgress: (validationStates.anthropic === 'valid' || validationStates.openai === 'valid' || validationStates.gemini === 'valid' || validationStates.xai === 'valid' || validationStates.openrouter === 'valid') 
              && validationStates.anthropic !== 'validating' 
              && validationStates.openai !== 'validating'
              && validationStates.gemini !== 'validating'
              && validationStates.xai !== 'validating'
              && validationStates.openrouter !== 'validating',
            disabledReason: validationStates.anthropic === 'validating' || validationStates.openai === 'validating' || validationStates.gemini === 'validating' || validationStates.xai === 'validating' || validationStates.openrouter === 'validating'
              ? 'Please wait while validating API keys...'
              : (validationStates.anthropic !== 'valid' && validationStates.openai !== 'valid' && validationStates.gemini !== 'valid' && validationStates.xai !== 'valid' && validationStates.openrouter !== 'valid') 
                ? 'Add at least one valid API key to continue' 
                : undefined
          })}
        </div>
      </div>
    </div>
  )
}

ApiKeyConfiguration.propTypes = {
  renderNavigation: PropTypes.func.isRequired,
  renderProgress: PropTypes.func.isRequired,
  navigationProps: PropTypes.object.isRequired,
  progressProps: PropTypes.object.isRequired
}

export default ApiKeyConfiguration
